import { useCallback, useEffect, useRef, useState } from "react";
import { CookService } from "../../services/CookServices";
import { OneMealService } from "../../services/OneMealService";
import { Box, Button, Link, Tooltip } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import EventBus from "../../EventBus";
import { formatDate, formatDateTime } from "../../utils/DateTimeUtils";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

interface InputProps {
  cookId: number;
}

type DeniedVisits = {
  id: number;
  cookId: number;
  reason?: string;
  cancellationReason?: string;
  deniedBy?: number | string;
  deniedByType?: string;
  deniedAt?: string;
  oneMealServiceId?: number;
  referenceId?: number;
  referenceModelName?: string;
  isEmergency?: boolean;
  emergencyRevertedBy?: number;
  emergencyRevertedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  oneMealService?: {
    id?: number;
    cookId?: number;
    cookName?: string;
    customerName?: string;
    date?: string;
    time?: string;
    cityName?: string;
    areaName?: string;
    status?: string;
  };
};

type DeniedVisitsResponse = {
  data?: DeniedVisits[];
  count?: number;
  total?: number;
};

const DEFAULT_SORT = { order: "DESC", field: "id" };
const SKELETON_ROWS = 5;

const cookService = new CookService();
const oneMealService = new OneMealService();

const normalizeResponse = (result: unknown): { rows: DeniedVisits[]; total: number } => {
  if (Array.isArray(result)) {
    return { rows: result, total: result.length };
  }
  const typedResult = (result || {}) as DeniedVisitsResponse;
  const rows = Array.isArray(typedResult?.data) ? typedResult.data : [];
  const totalRows =
    typeof typedResult?.total === "number"
      ? typedResult.total
      : typeof typedResult?.count === "number"
        ? typedResult.count
        : rows.length;
  return { rows, total: totalRows };
};

const getErrorMessage = (err: unknown): string => {
  const typedError = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return (
    typedError?.response?.data?.error ||
    typedError?.response?.data?.message ||
    typedError?.message ||
    "Failed to fetch denied visits"
  );
};

const getDeniedByHref = (deniedBy: number | string, deniedByType?: string) => {
  if (!deniedByType) return null;
  if (deniedByType === "ADMIN") return `/#/AdminUsers/${deniedBy}/show`;
  if (deniedByType === "CUSTOMER") return `/#/Customers/${deniedBy}/show`;
  if (deniedByType === "COOK") return `/#/cooks/${deniedBy}/show`;
  return null;
};

const CookDenielVisitTab = (props: InputProps) => {
  const latestRequestRef = useRef(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [data, setData] = useState<DeniedVisits[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [revertingId, setRevertingId] = useState<number | null>(null);
  const [togglingEmergencyId, setTogglingEmergencyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDeniedVisits = useCallback(
    async (cookId: number, limit: number, offset: number) => {
      if (!cookId) {
        setData([]);
        setTotal(0);
        setError(null);
        setIsLoading(false);
        return;
      }
      const requestId = ++latestRequestRef.current;
      setIsLoading(true);
      setError(null);
      try {
        const result = await cookService.getAllCookDenielVisits(cookId, limit, offset, DEFAULT_SORT);
        if (requestId !== latestRequestRef.current) return;
        const normalizedData = normalizeResponse(result);
        setData(normalizedData.rows);
        setTotal(normalizedData.total);
      } catch (err: unknown) {
        if (requestId !== latestRequestRef.current) return;
        setData([]);
        setTotal(0);
        setError(getErrorMessage(err));
      } finally {
        if (requestId !== latestRequestRef.current) return;
        setIsLoading(false);
      }
    },
    []
  );

  const revertDeniedVisit = useCallback(
    async (row: DeniedVisits) => {
      const serviceId = row?.oneMealService?.id;
      const cookId = props?.cookId;
      console.log("Reverting denied visit for serviceId:", serviceId, "cookId:", cookId);
      if (!serviceId || !cookId) {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Unable to revert: missing service or cook id",
          type: CustomSnackbarTypes.ERROR,
        });
        return;
      }
      setRevertingId(row.id);
      try {
        await oneMealService.revertDeniedCook(serviceId, cookId);
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Denied visit reverted successfully",
          type: CustomSnackbarTypes.SUCCESS,
        });
        await fetchDeniedVisits(props.cookId, rowsPerPage, page * rowsPerPage);
      } catch (err: unknown) {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: getErrorMessage(err),
          type: CustomSnackbarTypes.ERROR,
        });
      } finally {
        setRevertingId(null);
      }
    },
    [fetchDeniedVisits, page, props.cookId, rowsPerPage]
  );

  const onClickRevert = useCallback(
    (row: DeniedVisits) => {
      EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
        title: "Revert Denial?",
        message: "Please confirm to revert this denied visit",
        successLabel: "Confirm",
        failureLabel: "Cancel",
        onClose: (confirmed: boolean) => {
          if (confirmed) revertDeniedVisit(row);
        },
      });
    },
    [revertDeniedVisit]
  );

  const toggleDenialEmergencyStatus = useCallback(
    async (row: DeniedVisits) => {
      setTogglingEmergencyId(row.id);
      try {
        await oneMealService.toggleDenialEmergencyStatus(row.id);
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Emergency status updated successfully",
          type: CustomSnackbarTypes.SUCCESS,
        });
        await fetchDeniedVisits(props.cookId, rowsPerPage, page * rowsPerPage);
      } catch (err: unknown) {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: getErrorMessage(err),
          type: CustomSnackbarTypes.ERROR,
        });
      } finally {
        setTogglingEmergencyId(null);
      }
    },
    [fetchDeniedVisits, page, props.cookId, rowsPerPage]
  );

  const onClickToggleEmergency = useCallback(
    (row: DeniedVisits) => {
      EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
        title: "Remove Emergency?",
        message: "Please confirm to remove emergency status from this denial",
        successLabel: "Confirm",
        failureLabel: "Cancel",
        onClose: (confirmed: boolean) => {
          if (confirmed) toggleDenialEmergencyStatus(row);
        },
      });
    },
    [toggleDenialEmergencyStatus]
  );

  useEffect(() => { setPage(0); }, [props.cookId]);

  useEffect(() => {
    fetchDeniedVisits(props.cookId, rowsPerPage, page * rowsPerPage);
  }, [fetchDeniedVisits, props.cookId, page, rowsPerPage]);

  useEffect(() => {
    if (isLoading || total <= 0) return;
    const lastPage = Math.max(Math.ceil(total / rowsPerPage) - 1, 0);
    if (page > lastPage) setPage(lastPage);
  }, [total, rowsPerPage, page, isLoading]);

  const columns: CommonListColumn<DeniedVisits>[] = [
    {
      label: 'ID',
      render: (row) => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {row?.id || "-"}
          {row?.isEmergency && (
            <Tooltip title="Emergency">
              <LocalFireDepartmentIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      label: 'Chefit ID',
      render: (row) => row?.oneMealService?.id ? (
        <Link href={`/#/OneMealService/${row?.oneMealService?.id}/show`} underline="hover">
          {row?.oneMealService?.id}
        </Link>
      ) : "-",
    },
    {
      label: 'Visit Date & Time',
      nowrap: true,
      render: (row) => row?.oneMealService?.date
        ? `${formatDate(row.oneMealService.date)}${row?.oneMealService?.time ? ` ${row.oneMealService.time}` : ""}`
        : "-",
    },
    { label: 'Status', render: (row) => row?.oneMealService?.status || "-" },
    { label: 'Reason', render: (row) => row?.cancellationReason || "-" },
    {
      label: 'Denied By',
      render: (row) => {
        if (!row?.deniedBy) return "-";
        const href = getDeniedByHref(row.deniedBy, row?.deniedByType);
        return href ? <Link href={href} underline="hover">{row.deniedBy}</Link> : <>{row.deniedBy}</>;
      },
    },
    { label: 'Denied By Type', render: (row) => row?.deniedByType || "-" },
    { label: 'Denied At', nowrap: true, render: (row) => formatDateTime(row?.deniedAt) || "-" },
    { label: 'Emergency Reverted By', render: (row) => row?.emergencyRevertedBy || "-" },
    { label: 'Emergency Reverted At', nowrap: true, render: (row) => formatDateTime(row?.emergencyRevertedAt) || "-" },
    {
      label: 'Action',
      stopRowClick: true,
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onClickRevert(row)}
          disabled={
            revertingId === row.id ||
            togglingEmergencyId === row.id ||
            !(row?.oneMealServiceId || row?.oneMealService?.id)
          }
        >
          {revertingId === row.id ? "Reverting..." : "Revert Denial"}
        </Button>
      ),
    },
    {
      label: 'Emergency Action',
      stopRowClick: true,
      render: (row) => row?.isEmergency ? (
        <Button
          size="small"
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 600 }}
          onClick={() => onClickToggleEmergency(row)}
          disabled={revertingId === row.id || togglingEmergencyId === row.id}
        >
          {togglingEmergencyId === row.id ? "Updating..." : "Remove Emergency"}
        </Button>
      ) : null,
    },
  ];

  return (
    <>
      {!isLoading && error && (
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="12px" p={2} mb={1} border="1px solid #fca5a5" borderRadius={1}>
          <span>{error}</span>
          <Button size="small" variant="outlined" onClick={() => fetchDeniedVisits(props.cookId, rowsPerPage, page * rowsPerPage)}>
            Retry
          </Button>
        </Box>
      )}
      <CommonListTable
        title="Denied Visits"
        columns={columns}
        data={data}
        loading={isLoading}
        skeletonRows={SKELETON_ROWS}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        emptyText="No denied visits found"
      />
    </>
  );
};

export default CookDenielVisitTab;
